import { Store } from '@ngrx/store';
import { Subscription, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { appState } from '../interfaces/appState.interface';
import { Order, OrderExchange } from '@shared-logic/orders';
import { Price } from '@shared-logic/prices';
import { SSENotificationTypes } from './notification-types.enum';
import { selectAllOrders, selectAllControlPanelOrders } from '../../redux/selectors/orders.selectors';
import { selectAllPrices } from '../../redux/selectors/prices.selectors';
import { selectAllExchanges } from '../../redux/selectors/exchanges.selectors';
import { selectLocation } from '../../redux/selectors/options.selector';
import { inject } from '@angular/core';
import { loadOrdersAllSuccess } from '../../redux/actions/order.action';
import { loadOrdersAllSuccessInControlPanel } from '../../redux/actions/control-panel.action';
import { loadPricesAllSuccess } from '../../redux/actions/price.action';
import { loadExchangesAllSuccess } from '../../redux/actions/exchange.action';
import { setLastUpdate } from 'src/app/redux/actions/options.action';
import { DataRefreshService } from '../services/data-refresh.service';
import { NAVEGATION } from '../const/navegation';
import { localStorageNames } from '../const/localStorageNames';

// Union types for SSE messages
export type SSEOrderMessage = Order & { notificationType: SSENotificationTypes.ORDERS_CREATE | SSENotificationTypes.ORDERS_EDIT };
export type SSEPriceMessage = Price & { notificationType: SSENotificationTypes.PRICES_CREATE | SSENotificationTypes.PRICES_EDIT | SSENotificationTypes.PRICES_DELETE };
export type SSEExchangeMessage = OrderExchange & { notificationType: SSENotificationTypes.EXCHANGES_CREATE | SSENotificationTypes.EXCHANGES_EDIT | SSENotificationTypes.EXCHANGES_DELETE };

export type SSEMessage = SSEOrderMessage | SSEPriceMessage | SSEExchangeMessage;

export class SSEFunctions {
  private eventSource!: EventSource;
  private isConnecting = false;
  private shouldReconnect = true;
  private reconnectTimeoutId: any = null;
  private wasDisconnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = -1; // -1 = infinite attempts

  // Heartbeat system to detect zombie connections
  private lastHeartbeat: number = Date.now();
  private heartbeatCheckInterval: any = null;
  private heartbeatTimeout: number = 20000; // 20 seconds timeout (backend sends ping every 15s)
  private heartbeatCheckFrequency: number = 5000; // Check every 5 seconds

  // Store subscriptions
  private orders$!: Subscription;
  private controlPanelOrders$!: Subscription;
  private prices$!: Subscription;
  private exchanges$!: Subscription;
  private options$!: Subscription;

  // Current data arrays
  private orders: Order[] = [];
  private controlPanelOrders: Order[] = [];
  private prices: Price[] = [];
  private exchanges: OrderExchange[] = [];
  private currentLocation = '';

  private store = inject(Store<appState>);
  private dataRefreshService = inject(DataRefreshService);

  constructor() {
    this.connect();
  }

  private connect(): void {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.log('Connection already in progress, skipping...');
      return;
    }

    this.isConnecting = true;

    // Close existing connection if any
    if (this.eventSource) {
      this.eventSource.close();
    }

    console.log(`Connecting to SSE... (attempt ${this.reconnectAttempts + 1})`);

    try {
      this.eventSource = new EventSource(environment.rutas.sse.events);

      this.eventSource.onopen = () => this.onOpen();
      this.eventSource.onmessage = (event) => this.onMessage(event);
      this.eventSource.onerror = (error) => this.onError(error);

    } catch (error) {
      console.error('Error creating EventSource:', error);
      this.isConnecting = false;
      this.handleConnectionFailure();
    }
  }

  private onOpen(): void {
    console.log('✅ SSE connection established successfully');

    // Reset connection state
    this.isConnecting = false;
    this.reconnectAttempts = 0;

    // Clear any pending reconnection timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Initialize heartbeat system
    this.startHeartbeatCheck();

    // If this is a reconnection (not initial connection), refresh data
    if (this.wasDisconnected) {
      console.log('🔄 Reconnected after disconnection, refreshing data...');
      this.refreshDataBasedOnLocation();
      this.wasDisconnected = false;
    }
  }

  private onMessage(event: MessageEvent): void {
    try {
      // Update heartbeat timestamp on any message received
      this.lastHeartbeat = Date.now();

      // Handle heartbeat/ping messages from server
      if (event.data === 'ping' || event.data === 'heartbeat') {
        console.log('💓 Received ping from server - connection healthy');
        return;
      }

      const message: SSEMessage = JSON.parse(event.data);
      console.log('📨 Received SSE data message:', message.notificationType);
      this.handleNotification(message);
    } catch (error) {
      console.error('Error parsing SSE message:', error);
      console.error('Raw message data:', event.data);
    }
  }

  private onError(error: Event): void {
    console.error('❌ SSE connection error:', error);

    this.isConnecting = false;

    // Check EventSource readyState to determine the type of error
    if (this.eventSource) {
      const readyState = this.eventSource.readyState;
      console.log(`EventSource readyState: ${readyState} (0=CONNECTING, 1=OPEN, 2=CLOSED)`);

      // Only handle reconnection if connection is truly closed or connecting failed
      if (readyState === EventSource.CLOSED || readyState === EventSource.CONNECTING) {
        this.handleConnectionFailure();
      }
    } else {
      this.handleConnectionFailure();
    }
  }

  private handleConnectionFailure(): void {
    // Mark as disconnected for future reference
    this.wasDisconnected = true;

    // Clear any existing timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Check if we should attempt to reconnect
    if (!this.shouldReconnect) {
      console.log('Reconnection disabled, not attempting to reconnect');
      return;
    }

    // Check if we've exceeded max attempts (if limit is set)
    if (this.maxReconnectAttempts > 0 && this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`❌ Max reconnection attempts (${this.maxReconnectAttempts}) exceeded`);
      return;
    }

    this.reconnectAttempts++;

    // Use exponential backoff for first few attempts, then stick to 5 seconds
    const delay = this.reconnectAttempts === 1 ? 1000 : 5000; // 1s for first retry, then 5s

    console.log(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms...`);

    this.reconnectTimeoutId = setTimeout(() => {
      if (this.shouldReconnect && !this.isConnecting) {
        console.log(`🔄 Attempting to reconnect to SSE (attempt ${this.reconnectAttempts})...`);
        this.connect();
      }
    }, delay);
  }

  private handleNotification(message: SSEMessage): void {
    switch (message.notificationType) {
      case SSENotificationTypes.ORDERS_CREATE:
        this.handleOrdersCreate(message as SSEOrderMessage);
        break;
      case SSENotificationTypes.ORDERS_EDIT:
        this.handleOrdersEdit(message as SSEOrderMessage);
        break;
      case SSENotificationTypes.PRICES_CREATE:
        this.handlePricesCreate(message as SSEPriceMessage);
        break;
      case SSENotificationTypes.PRICES_EDIT:
        this.handlePricesEdit(message as SSEPriceMessage);
        break;
      case SSENotificationTypes.PRICES_DELETE:
        this.handlePricesDelete(message as SSEPriceMessage);
        break;
      case SSENotificationTypes.EXCHANGES_CREATE:
        this.handleExchangesCreate(message as SSEExchangeMessage);
        break;
      case SSENotificationTypes.EXCHANGES_EDIT:
        this.handleExchangesEdit(message as SSEExchangeMessage);
        break;
      case SSENotificationTypes.EXCHANGES_DELETE:
        this.handleExchangesDelete(message as SSEExchangeMessage);
        break;
      default:
        console.warn('Unknown notification type:', (message as any).notificationType);
    }
  }

  // Event handlers - implemented with specific logic
  private handleOrdersCreate(order: SSEOrderMessage): void {
    console.log('Orders create event:', order);

    // Clone existing arrays
    const updatedOrders = structuredClone(this.orders);
    const updatedControlPanelOrders = structuredClone(this.controlPanelOrders);

    // Copy order structure and remove notificationType
    const newOrder: Order = JSON.parse(JSON.stringify(order)) as Order;
    delete (newOrder as any).notificationType;

    // Add to both arrays
    updatedOrders.push(newOrder);
    updatedControlPanelOrders.push(newOrder);

    // Update store
    this.updateOrdersInStore(updatedOrders);
  }

  private handleOrdersEdit(order: SSEOrderMessage): void {
    console.log('Orders edit event:', order);

    // Clone existing arrays
    const updatedOrders = structuredClone(this.orders);
    const updatedControlPanelOrders = structuredClone(this.controlPanelOrders);

    // Copy order structure and remove notificationType
    const editedOrder: Order = JSON.parse(JSON.stringify(order)) as Order;
    delete (editedOrder as any).notificationType;

    // Find and replace in orders array
    const orderIndex = updatedOrders.findIndex(o => o._id === editedOrder._id);
    if (orderIndex !== -1) {
      updatedOrders[orderIndex] = editedOrder;
    }

    // Find and replace in control panel array
    const controlPanelIndex = updatedControlPanelOrders.findIndex(o => o._id === editedOrder._id);
    if (controlPanelIndex !== -1) {
      updatedControlPanelOrders[controlPanelIndex] = editedOrder;
    }

    // Update store
    this.updateOrdersInStore(updatedOrders);
  }

  private handlePricesCreate(price: SSEPriceMessage): void {
    console.log('Prices create event:', price);

    // Clone existing array
    const updatedPrices = structuredClone(this.prices);

    // Copy price structure and remove notificationType
    const newPrice: Price = JSON.parse(JSON.stringify(price)) as Price;
    delete (newPrice as any).notificationType;

    // Add to array
    updatedPrices.push(newPrice);

    // Update store
    this.updatePricesInStore(updatedPrices);
  }

  private handlePricesEdit(price: SSEPriceMessage): void {
    console.log('Prices edit event:', price);

    // Clone existing array
    const updatedPrices = structuredClone(this.prices);

    // Copy price structure and remove notificationType
    const editedPrice: Price = JSON.parse(JSON.stringify(price)) as Price;
    delete (editedPrice as any).notificationType;

    // Find and replace in array
    const priceIndex = updatedPrices.findIndex(p => p._id === editedPrice._id);
    if (priceIndex !== -1) {
      updatedPrices[priceIndex] = editedPrice;
    }
    // Update store
    this.updatePricesInStore(updatedPrices);
  }

  private handlePricesDelete(price: SSEPriceMessage): void {
    console.log('Prices delete event:', price);

    // Clone existing array
    const updatedPrices = structuredClone(this.prices);

    // Copy price structure and remove notificationType to get clean price
    const priceToDelete: Price = JSON.parse(JSON.stringify(price)) as Price;
    delete (priceToDelete as any).notificationType;

    // Filter out the deleted price
    const filteredPrices = updatedPrices.filter(p => p._id !== priceToDelete._id);

    // Update store
    this.updatePricesInStore(filteredPrices);
  }

  private handleExchangesCreate(exchange: SSEExchangeMessage): void {
    console.log('Exchanges create event:', exchange);

    // Clone existing array
    const updatedExchanges = structuredClone(this.exchanges);

    // Copy exchange structure and remove notificationType
    const newExchange: OrderExchange = JSON.parse(JSON.stringify(exchange)) as OrderExchange;
    delete (newExchange as any).notificationType;

    // Add to array
    updatedExchanges.push(newExchange);

    // Update store
    this.updateExchangesInStore(updatedExchanges);
  }

  private handleExchangesEdit(exchange: SSEExchangeMessage): void {
    console.log('Exchanges edit event:', exchange);

    // Clone existing array
    const updatedExchanges = structuredClone(this.exchanges);

    // Copy exchange structure and remove notificationType
    const editedExchange: OrderExchange = JSON.parse(JSON.stringify(exchange)) as OrderExchange;
    delete (editedExchange as any).notificationType;

    // Find and replace in array
    const exchangeIndex = updatedExchanges.findIndex(e => e._id === editedExchange._id);
    if (exchangeIndex !== -1) {
      updatedExchanges[exchangeIndex] = editedExchange;
    }

    // Update store
    this.updateExchangesInStore(updatedExchanges);
  }

  private handleExchangesDelete(exchange: SSEExchangeMessage): void {
    console.log('Exchanges delete event:', exchange);

    // Clone existing array
    const updatedExchanges = structuredClone(this.exchanges);

    // Copy exchange structure and remove notificationType to get clean exchange
    const exchangeToDelete: OrderExchange = JSON.parse(JSON.stringify(exchange)) as OrderExchange;
    delete (exchangeToDelete as any).notificationType;

    // Filter out the deleted exchange
    const filteredExchanges = updatedExchanges.filter(e => e._id !== exchangeToDelete._id);

    // Update store
    this.updateExchangesInStore(filteredExchanges);
  }

  public initializeSubscriptions(): void {
    // Subscribe to orders store using selectors
    this.orders$ = this.store.select(selectAllOrders).subscribe((orders: Order[]) => {
      this.orders = orders;
    });

    // Subscribe to control panel orders store using selectors
    this.controlPanelOrders$ = this.store.select(selectAllControlPanelOrders).subscribe((orders: Order[]) => {
      this.controlPanelOrders = orders;
    });

    // Subscribe to prices store using selectors
    this.prices$ = this.store.select(selectAllPrices).subscribe((prices: Price[]) => {
      this.prices = prices;
    });

    // Subscribe to exchanges store using selectors
    this.exchanges$ = this.store.select(selectAllExchanges).subscribe((exchanges: OrderExchange[]) => {
      this.exchanges = exchanges;
    });

    // Subscribe to options to track current location
    this.options$ = this.store.select(selectLocation).subscribe((location) => {
      this.currentLocation = location;
    });
  }

  public destroySubscriptions(): void {
    this.orders$?.unsubscribe();
    this.controlPanelOrders$?.unsubscribe();
    this.prices$?.unsubscribe();
    this.exchanges$?.unsubscribe();
    this.options$?.unsubscribe();
  }

  public closeConnection(): void {
    console.log('🔌 Closing SSE connection...');

    // Disable reconnection
    this.shouldReconnect = false;
    this.isConnecting = false;

    // Clear any pending reconnection
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Stop heartbeat checking
    this.stopHeartbeatCheck();

    // Close the EventSource connection
    if (this.eventSource) {
      this.eventSource.close();
    }

    // Clean up subscriptions
    this.destroySubscriptions();
  }

  /**
   * Manually restart the SSE connection
   */
  public restartConnection(): void {
    console.log('🔄 Manually restarting SSE connection...');

    // Reset state
    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    this.wasDisconnected = false;

    // Close existing connection
    if (this.eventSource) {
      this.eventSource.close();
    }

    // Clear any pending reconnection
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Reconnect
    this.connect();
  }

  /**
   * Get current connection status
   */
  public getConnectionStatus(): {
    isConnected: boolean;
    isConnecting: boolean;
    readyState: number | null;
    reconnectAttempts: number;
    lastHeartbeat: number;
    timeSinceLastHeartbeat: number;
    isHeartbeatHealthy: boolean;
  } {
    const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
    return {
      isConnected: this.eventSource?.readyState === EventSource.OPEN,
      isConnecting: this.isConnecting,
      readyState: this.eventSource?.readyState || null,
      reconnectAttempts: this.reconnectAttempts,
      lastHeartbeat: this.lastHeartbeat,
      timeSinceLastHeartbeat,
      isHeartbeatHealthy: timeSinceLastHeartbeat < this.heartbeatTimeout
    };
  }

  // Helper functions to update store
  private updateOrdersInStore(orders: Order[]): void {
    // Update both order and control panel stores
    this.store.dispatch(loadOrdersAllSuccess({ orders }));
    this.store.dispatch(loadOrdersAllSuccessInControlPanel({ orders }));
    this.setLocalStorageSafely(localStorageNames.ordersAll, orders);

    //this.store.dispatch(setLastUpdate({ lastUpdate: new Date() }));
  }

  private updatePricesInStore(prices: Price[]): void {
    this.store.dispatch(loadPricesAllSuccess({ prices }));
    //this.store.dispatch(setLastUpdate({ lastUpdate: new Date() }));
    this.setLocalStorageSafely(localStorageNames.pricesAll, prices);

  }

  private updateExchangesInStore(exchanges: OrderExchange[]): void {
    this.store.dispatch(loadExchangesAllSuccess({ orders: exchanges }));
    //this.store.dispatch(setLastUpdate({ lastUpdate: new Date() }));
    this.setLocalStorageSafely(localStorageNames.ordersExchange, exchanges);

  }

  private setLocalStorageSafely(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      const isQuotaExceeded = error instanceof DOMException && error.name === 'QuotaExceededError';
      if (isQuotaExceeded) {
        console.warn(`[localStorage] Quota exceeded while setting ${key}. Data will remain only in store.`);
        return;
      }
      throw error;
    }
  }

  /**
   * Refresh data via HTTP based on current location
   */
  private refreshDataBasedOnLocation(): void {
    console.log(`Refreshing data for location: ${this.currentLocation}`);

    switch (this.currentLocation) {
      case NAVEGATION.PRICE_BOARD:
        this.refreshPrices();
        break;

      case NAVEGATION.ORDER_BOARD:
        this.refreshOrders();
        break;

      case NAVEGATION.ORDER_EXCHANGE:
        this.refreshExchanges();
        break;

      case NAVEGATION.CONTROL_PANEL:
        this.refreshOrders(); // Control panel also uses orders
        break;

      default:
        console.log(`No data refresh needed for location: ${this.currentLocation}`);
        break;
    }
  }

  /**
   * Refresh orders via HTTP using DataRefreshService
   */
  private refreshOrders(): void {
    console.log('Refreshing orders via HTTP...');
    // Determine if we need to update control panel based on current location
    const updateControlPanel = this.currentLocation === NAVEGATION.CONTROL_PANEL;
    this.dataRefreshService.refreshOrders(updateControlPanel);
  }

  /**
   * Refresh prices via HTTP using DataRefreshService
   */
  private refreshPrices(): void {
    console.log('Refreshing prices via HTTP...');
    this.dataRefreshService.refreshPrices();
  }

  /**
   * Refresh exchanges via HTTP using DataRefreshService
   */
  private refreshExchanges(): void {
    console.log('Refreshing exchanges via HTTP...');
    this.dataRefreshService.refreshExchanges();
  }

  /**
   * Start heartbeat checking to detect zombie connections
   */
  private startHeartbeatCheck(): void {
    this.lastHeartbeat = Date.now();

    // Clear any existing heartbeat check
    if (this.heartbeatCheckInterval) {
      clearInterval(this.heartbeatCheckInterval);
    }

    this.heartbeatCheckInterval = setInterval(() => {
      this.checkHeartbeat();
    }, this.heartbeatCheckFrequency);

    console.log(`💓 Ping monitoring started (backend sends ping every 15s, timeout: ${this.heartbeatTimeout}ms, check every: ${this.heartbeatCheckFrequency}ms)`);
  }

  /**
   * Stop heartbeat checking
   */
  private stopHeartbeatCheck(): void {
    if (this.heartbeatCheckInterval) {
      clearInterval(this.heartbeatCheckInterval);
      this.heartbeatCheckInterval = null;
      console.log('💓 Heartbeat monitoring stopped');
    }
  }

  /**
   * Check if we've received a heartbeat recently
   */
  private checkHeartbeat(): void {
    const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;

    if (timeSinceLastHeartbeat > this.heartbeatTimeout) {
      console.warn(`⚠️ No ping received for ${timeSinceLastHeartbeat}ms (expected every 15s, timeout: ${this.heartbeatTimeout}ms)`);
      console.log('💀 Detected zombie connection due to missing ping, forcing reconnection...');

      // Force close the connection and reconnect
      this.stopHeartbeatCheck();

      if (this.eventSource) {
        this.eventSource.close();
      }

      // Mark as disconnected and trigger reconnection with data refresh
      this.wasDisconnected = true;
      this.isConnecting = false;

      if (this.shouldReconnect) {
        setTimeout(() => {
          console.log('🔄 Reconnecting after ping timeout and refreshing data...');
          this.connect();

          // Immediately refresh data since we detected a connection issue
          setTimeout(() => {
            console.log('🔄 Refreshing data after heartbeat reconnection...');
            this.refreshDataBasedOnLocation();
          }, 2000); // Wait 2s for connection to establish

        }, 1000);
      }
    }
  }
}